import React from "react";
import { Link } from "react-router-dom";
import AppHeader from "../components/AppHeader";
import SiteFooter from "../components/SiteFooter";
import DisclaimerBanner from "../components/DisclaimerBanner";
import { ChevronLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LAST_UPDATED = "February 7, 2026";
const VERSION = "2026-02-07";

const Section = ({ n, title, children }) => (
  <section className="mb-10" data-testid={`terms-section-${n}`}>
    <h2 className="font-serif-display text-2xl text-slate-900 mb-3">
      <span className="font-mono-tech text-sm text-slate-500 mr-3">{String(n).padStart(2, "0")}</span>
      {title}
    </h2>
    <div className="text-slate-700 leading-relaxed space-y-3 text-[15px]">{children}</div>
  </section>
);

export default function Terms() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <DisclaimerBanner />
      {user ? <AppHeader /> : (
        <header className="border-b border-slate-200">
          <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center">
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 underline underline-offset-4">
              <ChevronLeft className="w-4 h-4" /> Back to home
            </Link>
          </div>
        </header>
      )}
      <main className="max-w-[820px] mx-auto px-6 py-12 md:py-16 flex-1 w-full" data-testid="terms-page">
        <div className="label-tiny mb-3">Legal</div>
        <h1 className="font-serif-display text-4xl md:text-5xl leading-tight text-slate-900">Terms of Use</h1>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-500">
          <span data-testid="terms-last-updated">Last updated: <span className="tabular-nums text-slate-900 font-medium">{LAST_UPDATED}</span></span>
          <span aria-hidden="true">·</span>
          <span className="font-mono-tech text-xs">Version {VERSION}</span>
        </div>

        <div className="border-t border-slate-200 mt-8 pt-10">
          <Section n={1} title="Acceptance of Terms">
            <p>
              These Terms of Use govern your access to and use of ProSe.Claims, a technology
              platform operated by MisherTech (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By creating an
              account, signing in, or otherwise using the platform, you affirmatively agree
              to these Terms. If you do not agree, do not use the platform.
            </p>
          </Section>

          <Section n={2} title="Not a Law Firm; No Legal Advice">
            <p>
              ProSe.Claims is not a law firm and does not provide legal advice,
              representation, or referrals. Information and documents produced through the
              platform may be incomplete or inaccurate and require independent review by a
              licensed attorney. Use of ProSe.Claims does not create an attorney-client
              relationship, and no outcome is promised or guaranteed.
            </p>
          </Section>

          <Section n={3} title="Eligibility">
            <p>
              You must be at least 18 years old and legally capable of entering into a
              binding contract to use ProSe.Claims.
            </p>
          </Section>

          <Section n={4} title="Your Account">
            <p>
              You are responsible for maintaining the confidentiality of your account
              credentials and for all activity that occurs under your account. Notify us
              immediately of any unauthorized use.
            </p>
          </Section>

          <Section n={5} title="Acceptable Use">
            <p>You will not:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Use the platform to submit false, misleading, or fraudulent information;</li>
              <li>Impersonate any person or misrepresent your affiliation with any organization;</li>
              <li>Attempt to gain unauthorized access to other users&apos; data or accounts;</li>
              <li>Introduce malware or interfere with the platform&apos;s operation;</li>
              <li>Use the platform to harass, defame, or violate any third party&apos;s rights; or</li>
              <li>Scrape, reverse-engineer, or resell any part of the platform.</li>
            </ul>
          </Section>

          <Section n={6} title="Content You Submit">
            <p>
              You retain ownership of the content you submit (case facts, evidence, notes).
              You grant us a limited, non-exclusive license to store, process, and display
              your content solely to operate the platform for you.
            </p>
          </Section>

          <Section n={7} title="Documents Generated by the Platform">
            <p>
              Drafts, reports, and other documents generated by ProSe.Claims are working
              drafts for your review. They are not legal advice and must be independently
              reviewed before filing, signing, or serving. You are solely responsible for
              the accuracy, timing, and filing of any documents you use.
            </p>
          </Section>

          <Section n={8} title="Research &amp; Development Phase — Free Use">
            <p>
              ProSe.Claims is currently offered as a MisherTech Research &amp; Development
              initiative and is free to use during this phase. We are not charging for
              access, subscriptions, or document exports. If we introduce paid features in
              the future, we will notify you and update these Terms before any fees apply.
            </p>
          </Section>

          <Section n={9} title="Intellectual Property">
            <p>
              The platform, including its design, code, brand, and documentation, is owned
              by MisherTech and protected by intellectual-property laws. Your rights are
              limited to those expressly granted in these Terms.
            </p>
          </Section>

          <Section n={10} title="Disclaimers">
            <p>
              THE PLATFORM IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE.&rdquo; WE
              DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY,
              FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
          </Section>

          <Section n={11} title="Limitation of Liability">
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, MISHERTECH WILL NOT BE LIABLE FOR ANY
              INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR EXEMPLARY DAMAGES, OR FOR ANY
              LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF YOUR USE OF THE PLATFORM. OUR
              AGGREGATE LIABILITY WILL NOT EXCEED THE AMOUNTS YOU HAVE PAID US IN THE 12
              MONTHS BEFORE THE EVENT GIVING RISE TO LIABILITY.
            </p>
          </Section>

          <Section n={12} title="Indemnification">
            <p>
              You agree to indemnify and hold harmless MisherTech from any claim arising
              out of your use of the platform, your content, or your violation of these
              Terms or of any third party&apos;s rights.
            </p>
          </Section>

          <Section n={13} title="Termination">
            <p>
              We may suspend or terminate your access to the platform at any time for any
              reason, including violation of these Terms. You may terminate your account at
              any time by contacting us.
            </p>
          </Section>

          <Section n={14} title="Changes to These Terms">
            <p>
              We may update these Terms from time to time. When we make a material change,
              we will require you to review and accept the updated Terms before continuing
              to use the platform.
            </p>
          </Section>

          <Section n={15} title="Governing Law">
            <p>
              These Terms are governed by the laws of the State of California, without
              regard to conflict-of-law principles. Exclusive venue for disputes lies in
              the state and federal courts located in San Francisco County, California.
            </p>
          </Section>

          <Section n={16} title="Contact">
            <p>
              Questions about these Terms may be directed to
              {" "}<a href="mailto:corporate@mishertech.com" className="underline underline-offset-4">corporate@mishertech.com</a>.
            </p>
          </Section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
